import React, { useMemo } from 'react'
import {
    Button,
    Modal,
    ModalOverlay,
    ModalHeader,
    ModalContent,
    ModalBody,
    ModalFooter,
    Text,
    Tr,
    Td,
    Input,
    Flex,
    Checkbox,
    FormControl,
    useBreakpointValue
} from '@chakra-ui/react'
import List from '../../../../lib/list'
import Clean from '../../../../components/Clean'
import ErrorHelper from '../../../../components/ErrorHelper'
import ProductionItem from '../../../../models/production_item'
import ControlList from '../../../../lib/list/ControlList'

interface Props {
    open: boolean
    onClose: () => void
    handleAddProductionItems: (productionItems: ProductionItem[]) => void
    isSimple?: boolean
    listProductionItem: ControlList<ProductionItem>
    title: string
    selectedProductionItems?: ProductionItem[]
    size?:
        | 'xs'
        | 'sm'
        | 'md'
        | 'lg'
        | 'xl'
        | '2xl'
        | '3xl'
        | '4xl'
        | '5xl'
        | '6xl'
        | 'full'
}

export default function ModalProductionItems({
    open,
    onClose,
    isSimple = false,
    handleAddProductionItems,
    listProductionItem,
    title,
    selectedProductionItems = [],
    size = '2xl'
}: Props) {
    const finalRef = React.useRef<any>()

    // Mapear tamaños responsive
    const getResponsiveSize = (modalSize: string) => {
        const sizeMap: Record<string, any> = {
            xs: { base: 'full', sm: 'xs' },
            sm: { base: 'full', sm: 'sm' },
            md: { base: 'full', sm: 'md' },
            lg: { base: 'full', sm: 'lg' },
            xl: { base: 'full', sm: 'xl' },
            '2xl': { base: 'full', sm: 'xl', md: '2xl' },
            '3xl': { base: 'full', sm: 'xl', md: '2xl', lg: '3xl' },
            '4xl': { base: 'full', sm: 'xl', md: '3xl', lg: '4xl' },
            '5xl': { base: 'full', sm: 'xl', md: '3xl', lg: '5xl' },
            '6xl': { base: 'full', sm: 'xl', md: '4xl', lg: '6xl' },
            full: 'full'
        }
        return sizeMap[modalSize] || { base: 'full', sm: 'md' }
    }

    const responsiveSize = useBreakpointValue(getResponsiveSize(size))

    const [helper, setHelper] = React.useState<string>('')
    const [checkedProductionItems, setCheckedProductionItems] = React.useState<{
        [id: number]: boolean
    }>({})
    const [productionItems, setProductionItems] = React.useState<
        ProductionItem[]
    >([])

    const ids_production_items = useMemo(
        () => selectedProductionItems.map(data => data.id),
        [selectedProductionItems]
    )

    React.useEffect(() => {
        listProductionItem.load()
    }, [])

    const isValid = () => {
        let values = {}
        for (const [key, value] of Object.entries(checkedProductionItems)) {
            if (value) {
                values = {
                    ...values,
                    [key]: value
                }
            }
        }
        return Object.keys(values).length !== 0
    }

    const clearData = () => {
        setProductionItems([])
        setCheckedProductionItems({})
    }

    const handleSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
        ev.preventDefault()
        ev.stopPropagation()

        if (!isValid()) {
            setHelper('Debe seleccionar por lo menos uno')
            return
        }

        const _productionItems = []

        for (const [key, value] of Object.entries(checkedProductionItems)) {
            if (value) {
                const findProductionItem = productionItems.find(
                    data => Number(data.id) === Number(key)
                )
                if (findProductionItem) {
                    _productionItems.push(findProductionItem)
                }
            }
        }
        handleAddProductionItems(_productionItems)
        clearData()
    }

    const handleChange = (e: any, productionItem: ProductionItem) => {
        const value = e.target.checked
        const id = productionItem.id
        if (value) {
            setProductionItems(prev => [...prev, productionItem])
        }
        if (isSimple) {
            const findProductionItem = productionItems.find(
                data => data.id === id
            )
            if (findProductionItem) {
                if (!value) {
                    setCheckedProductionItems({})
                    return
                }
            }
            setCheckedProductionItems({
                [id]: value
            })
        } else {
            setCheckedProductionItems(prev => {
                return {
                    ...prev,
                    [id]: value
                }
            })
        }
    }

    const handleClose = () => {
        clearData()
        onClose()
    }

    const { clear, ...props } = listProductionItem.useSearch()

    return (
        <Modal
            finalFocusRef={finalRef}
            isOpen={open}
            onClose={handleClose}
            isCentered={true}
            size={responsiveSize}
            closeOnOverlayClick={false}
            scrollBehavior="inside"
            blockScrollOnMount={true}
            preserveScrollBarGap={true}
        >
            <ModalOverlay />
            <ModalContent
                px={{ base: 4, sm: 6 }}
                py={{ base: 3, sm: 4 }}
                maxH={{ base: '100vh', sm: '90vh' }}
                h={{ base: '100vh', sm: 'auto' }}
                mx={{ base: 0, sm: 4 }}
                my={{ base: 0, sm: 'auto' }}
                borderRadius={{ base: 0, sm: 'md' }}
            >
                <ModalHeader pb={{ base: 3, sm: 4 }} flexShrink={0}>
                    <Text fontSize={{ base: 'lg', sm: 'xl' }}>{title}</Text>
                </ModalHeader>
                <form
                    onSubmit={handleSubmit}
                    noValidate
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%'
                    }}
                >
                    <ModalBody
                        pb={{ base: 4, sm: 6 }}
                        overflowY="auto"
                        maxH={{
                            base: 'calc(100vh - 240px)',
                            sm: 'calc(90vh - 220px)'
                        }}
                        flex="1"
                    >
                        <FormControl mb={3}>
                            <ErrorHelper helper={helper} />
                        </FormControl>
                        <Flex mb="4">
                            <Clean
                                width={'full'}
                                value={props.value}
                                onClick={clear}
                            >
                                <Input
                                    placeholder="🔸 Buscar por nombre"
                                    {...props}
                                />
                            </Clean>
                        </Flex>
                        <List
                            control={listProductionItem}
                            tablesize="sm"
                            tbody={(productionItem, e) => {
                                const selected = ids_production_items.includes(
                                    productionItem.id
                                )

                                const isChecked =
                                    selected ||
                                    (checkedProductionItems[
                                        productionItem.id
                                    ] ??
                                        false)

                                return (
                                    <Tr key={e}>
                                        <Td textAlign="center" width="28">
                                            <Checkbox
                                                size="lg"
                                                isChecked={isChecked}
                                                disabled={selected}
                                                onChange={event =>
                                                    handleChange(
                                                        event,
                                                        productionItem
                                                    )
                                                }
                                            />
                                        </Td>
                                        <Td
                                            textAlign="center"
                                            fontSize={{ base: 'sm', sm: 'md' }}
                                        >
                                            {productionItem.name}
                                        </Td>
                                    </Tr>
                                )
                            }}
                        />
                    </ModalBody>
                    <ModalFooter
                        pt={{ base: 3, sm: 4 }}
                        pb={{ base: 3, sm: 4 }}
                        flexDirection={{ base: 'column', sm: 'row' }}
                        gap={{ base: 2, sm: 0 }}
                        flexShrink={0}
                    >
                        <Button
                            variant="alo-gray:lg-md"
                            bg="transparent"
                            color="alo-gray"
                            onClick={handleClose}
                            type="button"
                            width={{ base: 'full', sm: 'auto' }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="alo-green:lg-md"
                            ml={{ base: 0, sm: 3 }}
                            type="submit"
                            width={{ base: 'full', sm: 'auto' }}
                        >
                            Agregar
                        </Button>
                    </ModalFooter>
                </form>
            </ModalContent>
        </Modal>
    )
}
